import React, { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FloatIcon } from './CurrencyIcons';
import { iconUrl } from '../utils/assets';
import { useLocale } from '../i18n/LocaleContext';

/** Center modal in the visible Telegram viewport (not the full document). */
export function useViewportCenterStyle() {
  const [style, setStyle] = useState({});
  useEffect(() => {
    const apply = () => {
      const vv = window.visualViewport;
      if (!vv) {
        setStyle({});
        return;
      }
      setStyle({
        position: 'fixed',
        top: `${vv.offsetTop}px`,
        left: `${vv.offsetLeft}px`,
        width: `${vv.width}px`,
        height: `${vv.height}px`,
      });
    };
    apply();
    const vv = window.visualViewport;
    vv?.addEventListener?.('resize', apply);
    vv?.addEventListener?.('scroll', apply);
    window.addEventListener('resize', apply);
    return () => {
      vv?.removeEventListener?.('resize', apply);
      vv?.removeEventListener?.('scroll', apply);
      window.removeEventListener('resize', apply);
    };
  }, []);
  return style;
}

const AlertModal = memo(function AlertModal({
  open,
  title,
  body,
  icon = 'cross.png',
  have,
  need,
  currency,
  onClose,
  onConfirm,
  confirmLabel,
  cancelLabel,
  mode,
  destructive,
  busy,
  children,
}) {
  const { t } = useLocale();
  const overlayStyle = useViewportCenterStyle();
  if (!open) return null;

  const curLabel =
    currency === 'amber' || currency === 'rubies'
      ? t('rubies')
      : currency === 'coins'
        ? t('coins')
        : currency || '';

  const isConfirm = mode === 'confirm';

  const node = (
    <div
      className="alert-overlay"
      role="dialog"
      aria-modal="true"
      style={overlayStyle}
      onClick={isConfirm || busy ? undefined : onClose}
    >
      <div className="alert-modal glass-panel soft-card" onClick={(e) => e.stopPropagation()}>
        <FloatIcon src={iconUrl(icon)} size={64} className="alert-emoji" eager animated native />
        {title ? <h2 className="alert-title">{title}</h2> : null}
        {body ? <p className="alert-body">{body}</p> : null}
        {(have != null || need != null) && (
          <div className="alert-balances">
            <div className="alert-bal">
              <span className="alert-bal-label">{t('balanceNow')}</span>
              <span className="alert-bal-value">
                {have ?? '—'} {curLabel}
              </span>
            </div>
            <div className="alert-bal-arrow" aria-hidden>
              →
            </div>
            <div className="alert-bal need">
              <span className="alert-bal-label">{t('balanceNeed')}</span>
              <span className="alert-bal-value">
                {need ?? '—'} {curLabel}
              </span>
            </div>
          </div>
        )}
        {children}
        {isConfirm ? (
          <div className="alert-actions">
            <button type="button" className="action-btn stars soft-btn" disabled={busy} onClick={onClose}>
              {cancelLabel || t('close')}
            </button>
            <button
              type="button"
              className={`action-btn soft-btn ${destructive ? 'danger' : ''}`}
              disabled={busy}
              onClick={() => {
                if (busy) return;
                (onConfirm || onClose)?.();
              }}
            >
              {busy ? '…' : confirmLabel || t('gotIt')}
            </button>
          </div>
        ) : (
          <button type="button" className="action-btn alert-close soft-btn" disabled={busy} onClick={onClose}>
            {confirmLabel || t('close')}
          </button>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
});

export default AlertModal;
